import { observer } from 'mobx-react-lite'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'

import { useStore } from 'store/useStore'
import { ElockInfoStatus } from 'sections/LKSection/modules/ELockModule/types'
import { OrderKeys } from 'sections/LKSection/modules/OrderModule/data'
import { Order } from 'sections/LKSection/modules/OrderModule/stores/entity/Order'
import { ConfirmedToOpen, FurnitureOrderType } from 'sections/LKSection/modules/OrderModule/types'
import { getOrderItemPageKey, getOrderPageKey } from 'sections/LKSection/modules/OrderModule/utils'
import { CreateTicketModal } from 'sections/LKSection/modules/TicketModule/components/CreateTicketModal/CreateTicketModal'
import { Margin } from 'components/ui'
import { Button, Flex, Skeleton, Title } from 'components/uiKit'
import { getActivateELockRoute, getActivateOrderRoute } from 'utils/helpers/routes/getRoutes'
import { useAsyncEffect } from 'utils/hooks/useAsyncEffect'
import { Country } from 'shared/types/types'

import { ActivateWithoutELockModal } from '../../ActivateWithoutELockModal/ActivateWithoutELockModal'
import { OrderItemStatusHeader } from '../../OrderItemStatusHeader/OrderItemStatusHeader'
import { ConfirmOrderModal } from './ConfirmOrderModal/ConfirmOrderModal'
import { ELockOrderText } from './ELockOrderText/ELockOrderText'
import { FurnitureOrderText } from './FurnitureOrderText/FurnitureOrderText'

export type OrderStatusAcceptedLocationState = {
  fromActivateELockPage: boolean
}

type OrderStatusAcceptedProps = {
  order: Order
}

export const OrderStatusAccepted = observer(({ order }: OrderStatusAcceptedProps) => {
  const { viewOrderUiStore: uiStore } = useStore()

  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation()

  const fromActivateELockPage = (location.state as OrderStatusAcceptedLocationState)?.fromActivateELockPage

  const [isCreateTicketModalOpen, setIsCreateTicketModalOpen] = useState(false)
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false)
  const [isActivateWithoutELockModalOpen, setIsActivateWithoutELockModalOpen] = useState(Boolean(fromActivateELockPage))
  const [isELockSectionOpened, setIsELockSectionOpened] = useState(Boolean(fromActivateELockPage))
  const [isConfirmed, setIsConfirmed] = useState(false)
  const [loadingFurnitureOrderType, setLoadingFurnitureOrderType] = useState(false)
  const [furnitureOrderType, setFurnitureOrderType] = useState(FurnitureOrderType.none)

  const isDeleted = Boolean(order.deleted)
  const isMove = Boolean(order.exPickPointId)

  useAsyncEffect(async () => {
    if (order.confirmedToOpen === ConfirmedToOpen.none) {
      setIsConfirmModalOpen(true)
    }

    if (order.confirmedToOpen === ConfirmedToOpen.true) {
      setIsConfirmed(true)
    }

    if (order.id) {
      setLoadingFurnitureOrderType(true)
      const res = await uiStore.getFurnitureOrderType(order.id)
      setFurnitureOrderType(res ?? FurnitureOrderType.none)
      setLoadingFurnitureOrderType(false)
    }
  }, [order])

  const activateELockStepHandler = () => {
    if (order.id) {
      navigate(getActivateELockRoute(order.id))
    }
  }

  const activateOrderStep = () => {
    if (order.eLockInfo?.status === ElockInfoStatus.pending || order.country !== Country.RU) {
      if (order.id) {
        navigate(getActivateOrderRoute(order.id))
      }
    } else {
      setIsELockSectionOpened(true)
    }
  }

  const createTicketHandler = () => {
    setIsCreateTicketModalOpen(true)
  }

  const getKey = (key: string) => `${OrderKeys.orderItemPage}.${key}`

  return (
    <>
      {order.country === Country.RU && !order.exPickPointId ? (
        <>
          <OrderItemStatusHeader
            type="success"
            title={t(getOrderItemPageKey('step'), { count: 2 })}
            subTitle={t(getOrderItemPageKey('Оплачена'))}
          />
          <OrderItemStatusHeader
            type="success"
            title={t(getOrderItemPageKey('step'), { count: 3 })}
            subTitle={t(getOrderPageKey('Одобрена'))}
          />
          <OrderItemStatusHeader
            type="wait"
            title={t(getOrderItemPageKey('step'), { count: 4 })}
            subTitle={t(getKey('Требует отправки на активацию'))}
            isActive
          />
        </>
      ) : (
        <>
          <OrderItemStatusHeader
            type="success"
            title={t(getOrderItemPageKey('step'), { count: 2 })}
            subTitle={t(getOrderPageKey('Одобрена'))}
          />
          <OrderItemStatusHeader
            type="wait"
            title={t(getOrderItemPageKey('step'), { count: 3 })}
            subTitle={t(getKey('Требует отправки на активацию'))}
            isActive
          />
        </>
      )}

      {isConfirmed && (
        <Margin mt={32}>
          <Flex vertical gap={16}>
            <Title level={5}>
              {isELockSectionOpened && t(getKey('Установите и активируйте замок'))}

              {!isELockSectionOpened && t(getKey(isMove ? 'Ваша заявка на переезд одобрена' : 'Ваша заявка одобрена'))}
            </Title>

            <Skeleton loading={loadingFurnitureOrderType}>
              {isELockSectionOpened ? (
                <>
                  <ELockOrderText />

                  <Flex gap={8} vertical>
                    <Button
                      onClick={() => setIsActivateWithoutELockModalOpen(true)}
                      type="default"
                      disabled={isDeleted}
                    >
                      {t(getKey('Активация без замка'))}
                    </Button>
                    <Button onClick={activateELockStepHandler} disabled={isDeleted}>
                      {t(getKey('Активация с замком'))}
                    </Button>
                  </Flex>
                </>
              ) : (
                <>
                  <FurnitureOrderText furnitureOrderType={furnitureOrderType} isMove={Boolean(order.exPickPointId)} />

                  <Flex gap={8} vertical>
                    <Button onClick={activateOrderStep} disabled={isDeleted}>
                      {t(getKey('Перейти к активации ПВЗ'))}
                    </Button>
                    <Button onClick={createTicketHandler} newType="warning" disabled={isDeleted}>
                      {t(getKey('Обратиться в тех поддержку'))}
                    </Button>
                  </Flex>
                </>
              )}
            </Skeleton>
          </Flex>
        </Margin>
      )}

      <ConfirmOrderModal
        order={order}
        open={isConfirmModalOpen}
        isMove={isMove}
        setIsOpen={setIsConfirmModalOpen}
        setIsConfirmed={setIsConfirmed}
      />

      {order.id && (
        <ActivateWithoutELockModal
          orderId={order.id}
          open={isActivateWithoutELockModalOpen}
          setIsOpen={setIsActivateWithoutELockModalOpen}
        />
      )}

      <CreateTicketModal open={isCreateTicketModalOpen} setIsDeleteModalOpen={setIsCreateTicketModalOpen} />
    </>
  )
})
